import { useMemo } from 'react';
import { css } from '@emotion/react';
import {
  JustifiTransactionsSummary,
  Processor,
  StripeTransactionsSummary,
} from '@weave/schema-gen-ts/dist/schemas/payments-platform/payouts/v1';
import { Divider } from '@frontend/divider';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Heading, Table } from '@frontend/design-system';
import { useGetPayoutTransactionsV1Query } from '../../../hooks/use-get-payout';
import { formatCentsToCurrency } from '../../../utils/currency-helpers';
import { DetailCard } from '../../Card/detail-card';
import { PlaceholderSkeleton } from '../../PlaceholderSkeleton';
import { Value } from '../PayoutValue/value';
import { generateStripeColumns, generateJustifiColumns } from './generate-columns';

const PAYOUT_TABLE_LOADING_ROWS_COUNT = 4;

export const StripePayoutSummaryTable = ({
  stripeTransactionsSummary,
  isLoading,
}: {
  stripeTransactionsSummary?: StripeTransactionsSummary;
  isLoading: boolean;
}) => {
  const { t } = useTranslation('payments');
  const columns = useMemo(() => generateStripeColumns(t), []);

  return (
    <DetailCard>
      <Heading level={2}>{t('Summary')}</Heading>
      <Divider />
      <Table
        colConfig={columns}
        data={stripeTransactionsSummary?.payoutSummary?.payoutSummaryItems || []}
        emptyStateConfig={{
          type: 'payments',
          header: t('No data to display'),
        }}
        manualSortBy
        css={css`
          height: max-content;
        `}
        isLoading={isLoading}
        hasResponsiveColWidths
        // We know this will be 4 rows because there are
        // always 4 categories (charges, refunds, fees,
        // adjustments), and even zero value rows get
        // added to the summary table.
        loadingRowsCount={PAYOUT_TABLE_LOADING_ROWS_COUNT}
      />
      <div
        css={css`
          display: flex;
          flex-flow: row;
          justify-content: flex-end;
          margin: ${theme.spacing(3, 0)};
          > * {
            margin-left: ${theme.spacing(1)};
          }
        `}
      >
        <Value>{t('Payout Total')}</Value>
        {isLoading ? (
          <PlaceholderSkeleton loading width='8rem' height='1.75rem'>
            <span />
          </PlaceholderSkeleton>
        ) : (
          <Value>{formatCentsToCurrency(stripeTransactionsSummary?.payoutSummary?.totalGross)}</Value>
        )}
      </div>
    </DetailCard>
  );
};

export const JustifiPayoutSummaryTable = ({ payoutId, locationId }: { payoutId: string; locationId: string }) => {
  const { t } = useTranslation('payments');
  const columns = useMemo(() => generateJustifiColumns(t), []);
  const { data, isLoading } = useGetPayoutTransactionsV1Query({
    payoutId,
    locationId,
    processor: Processor.JUSTIFI,
  });

  const justifiTransactionsSummary: JustifiTransactionsSummary | undefined =
    data && 'justifiTransactionsSummary' in data && data?.justifiTransactionsSummary
      ? data?.justifiTransactionsSummary
      : undefined;

  return (
    <DetailCard>
      <Heading level={2}>{t('Summary')}</Heading>
      <Divider />
      <Table
        colConfig={columns}
        data={justifiTransactionsSummary?.summary?.payoutSummaryItems || []}
        emptyStateConfig={{
          type: 'payments',
          header: t('No data to display'),
        }}
        manualSortBy
        css={css`
          height: max-content;
        `}
        isLoading={isLoading}
        hasResponsiveColWidths
      />
      <div
        css={css`
          display: flex;
          flex-flow: row;
          justify-content: flex-end;
          margin: ${theme.spacing(3, 0)};
          > * {
            margin-left: ${theme.spacing(1)};
          }
        `}
      >
        <Value>{t('Payout Total')}</Value>
        {justifiTransactionsSummary && (
          <Value>
            {justifiTransactionsSummary.summary?.totalGross &&
              formatCentsToCurrency(justifiTransactionsSummary.summary.totalGross)}
          </Value>
        )}
      </div>
    </DetailCard>
  );
};
